import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Grid,
  Alert,
  FormControlLabel,
  Switch
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';

const EditOrganizationSubscriptionModal = ({
  open,
  onClose,
  subscription,
  subscriptionTypes = [],
  defaultSubscriptionsMap = new Map(),
  organizationSubscriptionCounts = new Map(),
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    customName: '',
    overridePrice: '',
    overrideDurationDays: '',
    overrideGracePeriodDays: '',
    notes: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  const selectedType = useMemo(() => {
    if (!subscription?.subscriptionTypeId) {
      return null;
    }
    return subscriptionTypes.find(type => type.id === subscription.subscriptionTypeId) || null;
  }, [subscription, subscriptionTypes]);

  useEffect(() => {
    if (subscription && open) {
      setFormData({
        customName: subscription.customName || '',
        overridePrice:
          subscription.overridePrice === null || subscription.overridePrice === undefined
            ? ''
            : String(subscription.overridePrice),
        overrideDurationDays:
          subscription.overrideDurationDays === null || subscription.overrideDurationDays === undefined
            ? ''
            : String(subscription.overrideDurationDays),
        overrideGracePeriodDays:
          subscription.overrideGracePeriodDays === null || subscription.overrideGracePeriodDays === undefined
            ? ''
            : String(subscription.overrideGracePeriodDays),
        notes: subscription.notes || '',
        status: subscription.status || 'active'
      });
      setError(null);
      setInfoMessage('');
    }
  }, [subscription, open]);

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatusToggle = (event) => {
    const isActive = event.target.checked;
    setFormData(prev => ({
      ...prev,
      status: isActive ? 'active' : 'inactive'
    }));
  };

  const buildUpdatePayload = () => {
    const payload = {
      customName: formData.customName.trim() ? formData.customName.trim() : null,
      notes: formData.notes.trim() ? formData.notes.trim() : null,
      status: formData.status
    };

    if (formData.overridePrice === '') {
      payload.overridePrice = null;
    } else {
      const price = parseFloat(formData.overridePrice);
      if (Number.isNaN(price) || price < 0) {
        throw new Error('Override price must be a non-negative number');
      }
      payload.overridePrice = price;
    }

    if (formData.overrideDurationDays === '') {
      payload.overrideDurationDays = null;
    } else {
      const duration = parseInt(formData.overrideDurationDays, 10);
      if (Number.isNaN(duration) || duration <= 0) {
        throw new Error('Override duration must be a positive integer');
      }
      payload.overrideDurationDays = duration;
    }

    if (formData.overrideGracePeriodDays === '') {
      payload.overrideGracePeriodDays = null;
    } else {
      const grace = parseInt(formData.overrideGracePeriodDays, 10);
      if (Number.isNaN(grace) || grace < 0) {
        throw new Error('Override grace period must be a non-negative integer');
      }
      payload.overrideGracePeriodDays = grace;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subscription?.id) {
      setError('Missing subscription identifier');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updates = buildUpdatePayload();
      const result = await subscriptionService.updateOrganizationSubscription(subscription.id, updates);
      const updated = result?.data || null;

      onSuccess?.(updated);
      onClose?.();
    } catch (err) {
      console.error('Error updating organization subscription:', err);
      setError(err.message || 'Failed to update organization subscription');
    } finally {
      setLoading(false);
    }
  };

  const isDefaultRecord = useMemo(() => Boolean(subscription?.isDefault), [subscription]);

  const isDefaultSubscriptionType = useMemo(() => {
    if (!subscription?.organizationId || !subscription?.subscriptionTypeId) {
      return false;
    }
    const defaultTypeId = defaultSubscriptionsMap.get(subscription.organizationId);
    return defaultTypeId === subscription.subscriptionTypeId;
  }, [subscription, defaultSubscriptionsMap]);

  const isOnlySubscription = useMemo(() => {
    if (!subscription?.organizationId) {
      return false;
    }
    const count = organizationSubscriptionCounts.get(subscription.organizationId) || 0;
    return count <= 1;
  }, [subscription, organizationSubscriptionCounts]);
  const isActive = formData.status !== 'inactive';
  const canTransferDefault = !isDefaultSubscriptionType && !isOnlySubscription && isActive;

  const handleSetDefault = async () => {
    if (!subscription?.organizationId || !subscription?.subscriptionTypeId) {
      setError('Missing organization or subscription type identifier');
      return;
    }

    try {
      if (!isDefaultRecord) {
        const confirmed = window.confirm(
          'Setting this subscription as default will move all sensors currently using the existing default to this subscription. Do you want to continue?'
        );
        if (!confirmed) {
          return;
        }
      }

      setDefaultLoading(true);
      setError(null);
      const result = await subscriptionService.setDefaultSubscription(
        subscription.organizationId,
        subscription.subscriptionTypeId
      );
      setInfoMessage('Default subscription updated successfully');
      onSuccess?.(result?.data || null);
    } catch (err) {
      console.error('Error setting default subscription:', err);
      setError(err.message || 'Failed to set default subscription');
    } finally {
      setDefaultLoading(false);
    }
  };

  if (!subscription) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!loading && !defaultLoading) {
          onClose?.();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <WalteroTypography
            variant="h5"
            sx={{ color: 'waltero.primary.coreBlue', fontWeight: 600 }}
          >
            Edit Organization Subscription
          </WalteroTypography>
          <WalteroTypography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Organization ID: {subscription.organizationId}
          </WalteroTypography>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {infoMessage && !error && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {infoMessage}
            </Alert>
          )}

          <Box sx={{ mb: 3, backgroundColor: 'grey.50', borderRadius: 1, p: 2 }}>
            <WalteroTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Subscription Details
            </WalteroTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Subscription Type
                </WalteroTypography>
                <WalteroTypography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedType ? selectedType.name : subscription.subscriptionTypeId || 'Unknown'}
                  {(isDefaultRecord || isDefaultSubscriptionType) && (
                    <WalteroTypography component="span" variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                      DEFAULT
                    </WalteroTypography>
                  )}
                </WalteroTypography>
              </Grid>
              <Grid item xs={6} md={3}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Base Price (per sensor / month)
                </WalteroTypography>
                <WalteroTypography variant="body2">
                  {selectedType?.basePrice ? `$${selectedType.basePrice}` : 'Not set'}
                </WalteroTypography>
              </Grid>
              <Grid item xs={6} md={3}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Base Duration
                </WalteroTypography>
                <WalteroTypography variant="body2">
                  {selectedType?.durationDays ? `${selectedType.durationDays} days` : 'Not set'}
                </WalteroTypography>
              </Grid>
              <Grid item xs={6} md={3}>
                <WalteroTypography variant="caption" color="text.secondary">
                  Base Grace Period
                </WalteroTypography>
                <WalteroTypography variant="body2">
                  {selectedType?.gracePeriodDays ? `${selectedType.gracePeriodDays} days` : '0 days'}
                </WalteroTypography>
              </Grid>
            </Grid>
          </Box>

          {isDefaultRecord && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This record represents the default subscription for the organization.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Custom Display Name"
                value={formData.customName}
                onChange={handleFieldChange('customName')}
                disabled={loading}
                placeholder="Leave empty to use subscription type name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Override Price"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.overridePrice}
                onChange={handleFieldChange('overridePrice')}
                disabled={loading}
                placeholder="Leave empty to use base price"
                helperText="Per sensor per month"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Override Duration (Days)"
                type="number"
                inputProps={{ min: 1 }}
                value={formData.overrideDurationDays}
                onChange={handleFieldChange('overrideDurationDays')}
                disabled={loading}
                placeholder="Leave empty to use base duration"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Override Grace Period (Days)"
                type="number"
                inputProps={{ min: 0 }}
                value={formData.overrideGracePeriodDays}
                onChange={handleFieldChange('overrideGracePeriodDays')}
                disabled={loading}
                placeholder="Leave empty to use base grace period"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={handleStatusToggle}
                    disabled={loading || isDefaultRecord}
                    color="primary"
                  />
                }
                label={isActive ? 'Subscription Active' : 'Subscription Inactive'}
              />
              {isDefaultRecord && (
                <WalteroTypography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Default subscription must remain active.
                </WalteroTypography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleFieldChange('notes')}
                disabled={loading}
                multiline
                rows={3}
                placeholder="Optional notes or context"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <WalteroButton
              variant="outlined"
              color="primary"
              onClick={handleSetDefault}
            disabled={
              defaultLoading ||
              loading ||
              !subscription.subscriptionTypeId ||
              !canTransferDefault
            }
          >
            {defaultLoading ? 'SETTING DEFAULT...' : 'SET AS DEFAULT'}
          </WalteroButton>
          </Box>

          <WalteroButton
            onClick={() => {
              if (!loading && !defaultLoading) {
                onClose?.();
              }
            }}
            variant="outlined"
            disabled={loading || defaultLoading}
          >
            CANCEL
          </WalteroButton>
          <WalteroButton
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'SAVING...' : 'SAVE CHANGES'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditOrganizationSubscriptionModal;
